/**
 * relativeTime.js — göreli zaman ("2 saat önce" / "2 hours ago") etiketleri
 * için TEK kaynak (single source of truth).
 *
 * KÖK NEDEN (bug): Bu formatlayıcı üç ayrı yerde bağımsız olarak elle
 * kopyalanmıştı:
 *   - components/notifications/NotificationRow.jsx  → dil'e HİÇ bakmadan
 *     İngilizce kısaltma üretiyordu ("2h", "5m", "3d") — TR arayüzde bile
 *     (yalnızca "az önce" eşiği i18n'liydi, dakika/saat/gün eşikleri değildi).
 *     Bu, bildirim listesinde görülen "2h ago" tarzı hatanın kök nedeniydi.
 *   - pages/AdminDashboard.jsx (`timeAgo`)          → dil'e bakıyordu ama TEK
 *     harfli kısaltmalar üretiyordu ("2s önce", "5d önce" — "s"=saat, "d"=dakika
 *     karışıklığı riski + kanonik tam kelime formatından sapma).
 *   - pages/AdminStudentHub/VideoProgressModal.jsx  → TR string'lerde Türkçe
 *     karakter eksikti ("Az once", "dk once" — "önce" değil).
 *
 * Bu modül tüm göreli-zaman etiketlerinin TEK kaynağıdır; yukarıdaki üç kopya
 * bu modüle yönlendirildi ve silindi.
 *
 * DİKKAT — falsy-zero tuzağı: diff saniye/dakika/... değerlerinden hiçbiri
 * `value || FALLBACK` kalıbıyla ele alınmaz — `0` burada anlamlı bir sınır
 * değeridir ("az önce" tam olarak <60sn demektir). Tüm karşılaştırmalar açık
 * `<` / `===` operatörleriyle yapılır.
 */

const SECOND_THRESHOLDS = {
  MINUTE: 60,
  HOUR: 60 * 60,
  DAY: 60 * 60 * 24,
  WEEK: 60 * 60 * 24 * 7,
  MONTH: 60 * 60 * 24 * 30,
  YEAR: 60 * 60 * 24 * 365,
};

function pluralEn(n, unit) {
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
}

const STRINGS = {
  tr: {
    justNow: 'az önce',
    minute: (n) => `${n} dakika önce`,
    hour: (n) => `${n} saat önce`,
    day: (n) => `${n} gün önce`,
    week: (n) => `${n} hafta önce`,
    month: (n) => `${n} ay önce`,
    year: (n) => `${n} yıl önce`,
  },
  en: {
    justNow: 'just now',
    minute: (n) => pluralEn(n, 'minute'),
    hour: (n) => pluralEn(n, 'hour'),
    day: (n) => pluralEn(n, 'day'),
    week: (n) => pluralEn(n, 'week'),
    month: (n) => pluralEn(n, 'month'),
    year: (n) => pluralEn(n, 'year'),
  },
};

/**
 * Dar tablo/liste hücreleri için ui-ux onaylı KISA form tablosu.
 *
 * Yalnızca en sık görülen üç eşiği (az-önce/dakika/saat/gün) kapsar — bunlar
 * ui-ux tarafından onaylanan, elle uydurulmamış tek kısaltma seti. hafta/ay/yıl
 * için KASITLI OLARAK kısaltma tanımlanmadı (ui-ux onayı yok); `formatRelativeTime`
 * bu iki (week/month/year) durumda otomatik olarak TAM (uzun) metne düşer —
 * bkz. aşağıdaki `{ ...STRINGS[...], ...STRINGS_SHORT[...] }` merge'i.
 *
 * TR kuralları:
 *   - "önce" kelimesi HER ZAMAN kalır (yalın "23dk" belirsiz — önce mi sonra mı).
 *   - Saat için "s" KULLANILMAZ (saniye ile karışır) → "sa".
 *   - Dakika için "d" KULLANILMAZ (gün ile karışır) → "dk".
 *   - Gün=1 özel durumu: "dün" (gün eşiğinin EN sık görülen değeri).
 * EN kuralları:
 *   - "ago" düşer (kısa formatlarda İngilizce'de zaten yaygın konvansiyon).
 *   - gün=1 için de aynı `${n}d` formülü kullanılır (n=1 → "1d"), özel durum
 *     gerekmez çünkü "1d" zaten en kısa/en net biçim.
 */
const STRINGS_SHORT = {
  tr: {
    justNow: 'az önce',
    minute: (n) => `${n} dk önce`,
    hour: (n) => `${n} sa önce`,
    day: (n) => (n === 1 ? 'dün' : `${n} g önce`),
  },
  en: {
    justNow: 'now',
    minute: (n) => `${n}m`,
    hour: (n) => `${n}h`,
    day: (n) => `${n}d`,
  },
};

/**
 * `dateInput`'u geçerli bir `Date`'e çevirir; geçersiz/boş girdide `null` döner.
 * NOT: `dateInput === 0` (epoch 1970-01-01) TEKNİK olarak geçerli bir tarihtir,
 * bu yüzden `!dateInput` gibi bir falsy kontrolü YAPILMAZ — yalnızca
 * null/undefined/'' açıkça elenir, `0` bir Date'e çevrilip test edilir.
 */
function resolveDate(dateInput) {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  const dt = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function resolveLang(lang) {
  // Yalnızca 'tr' açıkça TR string tablosunu seçer; tanımsız/başka her değer
  // (undefined, null, 'en', bilinmeyen kod) EN'e düşer — `lang || 'en'` YERİNE
  // açık eşitlik kontrolü (lang bir gün falsy-ama-geçerli bir değer olursa
  // örn. sayısal locale kodu, sessiz kırılma riskini önler).
  return lang === 'tr' ? 'tr' : 'en';
}

/**
 * Bir tarihi "şimdiye göre" göreli bir metne çevirir (TR/EN i18n-aware).
 *
 * @param {string|number|Date|null|undefined} dateInput - ISO string, epoch ms
 *   veya Date instance.
 * @param {string} lang - 'tr' | 'en' (başka/eksik değer → 'en').
 * @param {{ now?: Date, short?: boolean }} [options] - `now`: test için sabit
 *   "şimdi" enjeksiyonu. `short`: dar tablo/liste hücreleri için ui-ux onaylı
 *   kısaltma formu (yalnızca az-önce/dakika/saat/gün eşikleri kısalır; hafta/
 *   ay/yıl kasıtlı olarak tam metne düşer — bkz. STRINGS_SHORT yorumu).
 *   Açık `=== true` kontrolü kullanılır (`options.short || false` DEĞİL) —
 *   opsiyonel parametrelerde falsy-ama-geçerli bir değer sessizce yanlış
 *   dallanmaya yol açmasın diye.
 * @returns {string} Geçersiz/boş girdide '' (boş string) döner.
 */
export function formatRelativeTime(dateInput, lang, options = {}) {
  const dt = resolveDate(dateInput);
  if (dt === null) return '';

  const langKey = resolveLang(lang);
  const isShort = options.short === true;
  const strings = isShort
    ? { ...STRINGS[langKey], ...STRINGS_SHORT[langKey] }
    : STRINGS[langKey];
  const now = options.now instanceof Date ? options.now : new Date();
  const diffMs = now.getTime() - dt.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  // Gelecek tarih / küçük saat kayması → negatif farkı "az önce" gibi ele al.
  const safeDiffSec = diffSec < 0 ? 0 : diffSec;

  if (safeDiffSec < SECOND_THRESHOLDS.MINUTE) return strings.justNow;
  if (safeDiffSec < SECOND_THRESHOLDS.HOUR) {
    return strings.minute(Math.floor(safeDiffSec / SECOND_THRESHOLDS.MINUTE));
  }
  if (safeDiffSec < SECOND_THRESHOLDS.DAY) {
    return strings.hour(Math.floor(safeDiffSec / SECOND_THRESHOLDS.HOUR));
  }
  if (safeDiffSec < SECOND_THRESHOLDS.WEEK) {
    return strings.day(Math.floor(safeDiffSec / SECOND_THRESHOLDS.DAY));
  }
  if (safeDiffSec < SECOND_THRESHOLDS.MONTH) {
    return strings.week(Math.floor(safeDiffSec / SECOND_THRESHOLDS.WEEK));
  }
  if (safeDiffSec < SECOND_THRESHOLDS.YEAR) {
    return strings.month(Math.floor(safeDiffSec / SECOND_THRESHOLDS.MONTH));
  }
  return strings.year(Math.floor(safeDiffSec / SECOND_THRESHOLDS.YEAR));
}

/**
 * Bir tarihin tam/mutlak gösterimini döner (ör. `<time title>` veya tooltip
 * için) — locale'e göre `toLocaleString`.
 *
 * @param {string|number|Date|null|undefined} dateInput
 * @param {string} lang - 'tr' | 'en'
 * @returns {string|undefined} Geçersiz/boş girdide `undefined`.
 */
export function formatAbsoluteDateTime(dateInput, lang) {
  const dt = resolveDate(dateInput);
  if (dt === null) return undefined;
  try {
    return dt.toLocaleString(resolveLang(lang) === 'tr' ? 'tr-TR' : 'en-US');
  } catch {
    return undefined;
  }
}

/**
 * `dateInput`'u ISO 8601 string'e çevirir — `<time dateTime={...}>` için.
 *
 * @param {string|number|Date|null|undefined} dateInput
 * @returns {string|undefined} Geçersiz/boş girdide `undefined`.
 */
export function toIsoString(dateInput) {
  const dt = resolveDate(dateInput);
  if (dt === null) return undefined;
  return dt.toISOString();
}
